import type { User, UserDetails } from '@blinq-types';
import type { LoginMethodPayload } from '@modules/auth-helpers';
import type { Dispatch } from 'react';

export type FavouredEmail = 'work' | 'personal' | 'unknown';

export type LoginMode = 'signup' | 'login';

export enum LoginState {
  SIGN_IN_OR_SIGN_UP,
  FINISH_SIGNING_IN_PASSWORD,
  FINISH_SIGNING_IN_OTP,
  RESET_PASSWORD,
  CREATE_A_BLINQ_ACCOUNT,
  FINISH_SIGNING_UP_OTP,
  FINISH_SIGNING_UP_PASSWORD,
  CATASTROPHIC_ERROR,
}

export interface LoginFormProps {
  email: string;
  onEmailChange: (newValue: string) => void;
  password: string;
  onPasswordChange: (newValue: string) => void;
  beforeLoginHook?: (email: string) => Promise<string | undefined>;
  /**
   * Modes provide different functionality when the LoginForm loads
   * login performs a login
   * reset displays password reset flow
   * link used for linking new credentials to the current firebase user
   * hybrid a login or signup method that combines login and link modes above
   */
  mode: 'login' | 'reset' | 'link' | 'hybrid';
  onShowReset: () => void;
  onShowLogin: () => void;
  onLogin?: (user: UserDetails, mode: LoginMode) => void | Promise<void>;
  onLink?: () => void | Promise<void>;
  /**
   * User details that we want to add to a newly created Blinq user (hybrid or link modes)
   */
  newUserDetails?: Partial<User>;
  enableClearEmailButton?: boolean;
  /**
   * Pins the form to the address it was given. For re-authentication prompts, where signing in as
   * somebody else is never the intent. Note this constrains the email field only — the OAuth buttons
   * still go through the provider's own account picker, so callers that need the identity guaranteed
   * must check it after `onLogin`.
   */
  lockEmail?: boolean;
  reuseAnonUserIfPossible?: boolean;
  /**
   * If a user enters their personal email address, show a prompt to switch to their work email address
   */
  preferWorkEmail?: boolean;
  /** Login method used to login, such as the login provider */
  authProfile: LoginMethodPayload;
  setAuthProfile: Dispatch<React.SetStateAction<LoginMethodPayload>>;
  /** Tracks whether user entered a personal email */
  enteredPersonalEmail?: boolean;
  setEnteredPersonalEmail?: Dispatch<React.SetStateAction<boolean>>;
  /** Tracks what email the user favours when signing up, be it their personal or work email address */
  favouredEmail: FavouredEmail | undefined;
  setFavouredEmail: Dispatch<React.SetStateAction<FavouredEmail | undefined>>;
  setLoginState?: (state: LoginState) => void;
  loginState?: LoginState;
  /** Refers to the page from which the login form was accessed */
  refPage?: 'onboarding_signup' | 'email_sso' | 'otp_signin' | 'otp_signup';
  /**
   * Use popup-based OAuth instead of redirect-based OAuth. This keeps the current page
   * in place, preserving window.opener for flows that depend on cross-window communication
   * (e.g. HubSpot seamless install popup).
   */
  usePopupAuth?: boolean;
}
